// 1-on-1 goals card (Oct 2, 2026) — read-only. Goals are set in one place: the
// Crew page's ✏️ Set goals drawer (the four weekly columns + the month's build
// goal). This card shows what's set there for the selected person, with this
// week's and this month's progress, so the 1-on-1 can talk to the numbers.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { currentWeekStart, fetchWeeklyGoalsMap, type GoalMetricId } from "../../crew/crewQueries";
import { addDaysISO } from "../../../lib/weeklySpiffs";
import {
  fetchMonthBuildGoals, fetchMonthBuilds, isFirstMonday, monthName, monthStartOf, todayET,
} from "../../../lib/dailyCommitments";

const WEEK_METRICS: { id: GoalMetricId; label: string; col: "apps" | "claims" | "builds_scheduled" | "builds" }[] = [
  { id: "apps", label: "Apps", col: "apps" },
  { id: "claims", label: "Claims", col: "claims" },
  { id: "builds_scheduled", label: "Signed IA", col: "builds_scheduled" },
  { id: "builds", label: "Builds", col: "builds" },
];

function prevMonth(ms: string): string {
  const [y, m] = ms.split("-").map(Number);
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, "0")}-01`;
}
const fmt = (n: number | null | undefined) => (n == null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function MondayGoalsCard({ userId, role }: { userId: string; role: string; managerId?: string | null }) {
  const today = todayET();
  const monthStart = monthStartOf(today);
  const weekStart = currentWeekStart();
  const firstMonday = isFirstMonday(today);
  const builds = role !== "canvasser";

  const [weekGoals, setWeekGoals] = useState<Partial<Record<GoalMetricId, number>>>({});
  const [weekDone, setWeekDone] = useState<Record<string, number>>({});
  const [monthGoal, setMonthGoal] = useState<number | null>(null);
  const [month, setMonth] = useState<{ soFar: number; last: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const [wk, goals, cur, last, pts] = await Promise.all([
          fetchWeeklyGoalsMap(weekStart),
          fetchMonthBuildGoals(monthStart),
          fetchMonthBuilds(monthStart),
          fetchMonthBuilds(prevMonth(monthStart)),
          supabase.from("crew_daily_points").select("apps, claims, builds_scheduled, builds")
            .eq("user_id", userId).gte("day", weekStart).lt("day", addDaysISO(weekStart, 7)),
        ]);
        if (pts.error) throw pts.error;
        if (cancelled) return;
        setWeekGoals(wk[userId] ?? {});
        const done: Record<string, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
        for (const r of pts.data ?? []) for (const k of Object.keys(done)) done[k] += Number((r as Record<string, unknown>)[k]) || 0;
        setWeekDone(done);
        setMonthGoal(goals[userId] ?? null);
        setMonth({ soFar: cur[userId] ?? 0, last: last[userId] ?? 0 });
      } catch (e) {
        if (!cancelled) setErr((e as Error).message ?? "Couldn't load goals.");
      }
    })();
    return () => { cancelled = true; };
  }, [userId, weekStart, monthStart, tick]);

  // Goals edited on the Crew page show up here live.
  useEffect(() => {
    const ch = supabase.channel(`oneone-goals-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "crew_goals", filter: `user_id=eq.${userId}` }, () => setTick((n) => n + 1))
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [userId]);

  const monthMissing = builds && monthGoal == null;

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-900">🎯 Goals</div>
        <Link to="/crew" className="text-[11px] font-semibold text-orange-600 hover:underline">
          Set on the Crew page → ✏️ Set goals
        </Link>
      </div>

      {builds && (
        <div className={`rounded-lg p-3 ${monthMissing ? "border-2 border-orange-300 bg-orange-50" : "border border-slate-200 bg-slate-50"}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-bold text-slate-900">{monthName(monthStart)} build goal</div>
              <div className="text-[11px] text-slate-600">
                {month ? <>Last month {fmt(month.last)} builds</> : "Loading…"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black tabular-nums text-slate-900">
                {fmt(month?.soFar ?? 0)}<span className="text-sm font-semibold text-slate-400"> / {fmt(monthGoal)}</span>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">built so far</div>
            </div>
          </div>
          {monthMissing && (
            <div className="mt-1 text-[11px] font-semibold text-orange-700">
              {firstMonday ? "First Monday — " : ""}No {monthName(monthStart)} build goal yet. Set it on the Crew page (last column of ✏️ Set goals).
            </div>
          )}
        </div>
      )}

      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">This week · done / goal</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {WEEK_METRICS.filter((m) => builds || m.id === "apps").map((m) => {
            const g = weekGoals[m.id];
            const d = weekDone[m.col] ?? 0;
            const hit = g != null && g > 0 && d >= g;
            return (
              <div key={m.id} className={`rounded-lg border p-2 text-center ${hit ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{m.label}</div>
                <div className="text-lg font-black tabular-nums text-slate-900">{fmt(d)}<span className="text-sm font-semibold text-slate-400"> / {fmt(g)}</span></div>
              </div>
            );
          })}
        </div>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
    </div>
  );
}
